import React from 'react';

import { useRouter } from 'next/router';

import AdminSidebar from '@OnArk/admin/sidebar/AdminSidebar';
import { beaconLogout } from '@OnArk/utils/beacon';
import { secureCookie } from '@OnArk/utils/cookieSecure';
import posthog from '@OnArk/utils/posthog';
import { withClient } from '@OnArk/withGraph';
import Cookies from 'universal-cookie';

function AdminNav({
  church,
  client,
  user,
}: {
  church: WithAuthChurch;
  client: Sdk;
  user: WithAuthUser;
}) {
  const cookies = new Cookies();
  const router = useRouter();
  // NOTE: previously we registered a cookies.addChangeListener here that
  // reloaded the page on any cookie change. That fired on unrelated
  // writes (e.g. PostHog analytics cookies) and would navigate away
  // before async auth flows finished. changeChurch + logout below
  // navigate explicitly now, so the listener is no longer needed.
  const logout = (e?: React.MouseEvent) => {
    e?.preventDefault();
    cookies.set('onark-token', '', { path: '/', secure: secureCookie() });
    window.location.href = '/login';
    posthog?.capture('logout');
    beaconLogout();
  };

  const changeChurch = async (churchId: string) => {
    try {
      const { data } = await client.changeChurch({ churchId });

      if (!data?.token) throw new Error('No token returned from changeChurch');
      cookies.set('onark-token', data.token, {
        path: '/',
        secure: secureCookie(),
      });
      // Don't rely on the cookie change listener — navigate explicitly so
      // the new church's data loads. window.location forces a full reload
      // which is what we want here (clears React state, re-runs SSR).
      window.location.href = '/admin/classes';
    } catch (e: unknown) {
      // If the session has expired or the cookie is gone (e.g. another
      // tab logged out), the API throws UNAUTHENTICATED. Bounce to login
      // rather than leaving the user on a half-broken page.
      const msg = e instanceof Error ? e.message : '';

      if (/UNAUTHENTICATED|Not authenticated/i.test(msg)) {
        window.location.href = '/login';

        return;
      }
      throw e;
    }
  };

  return (
    <AdminSidebar
      asPath={router.asPath ?? ''}
      church={church}
      user={user}
      onChangeChurch={changeChurch}
      onLogout={logout}
    />
  );
}
export default withClient(AdminNav);
